<template>
  <div>
    <div class="space-y-4">
      <h2>{{ t('youngDrivers') }}</h2>
      <div class="grid grid-cols-3 gap-4">
        <InputField
          name="youngDriverUntilAge"
          :label="t('youngDriverUntilAge')"
          type="number"
          :min="0"
        />
      </div>
    </div>
    <Divider />
    <div class="space-y-4">
      <h2>{{ t('fueling') }}</h2>
      <div class="grid grid-cols-3 gap-4">
        <InputField
          name="fuelingPricePerLiter"
          :label="t('pricePerLiter')"
          type="number"
          :min="0"
          :max-fraction-digits="2"
          adornment="€"
        />
        <InputField
          name="chargingPricePerKwh"
          :label="t('pricePerKwh')"
          type="number"
          :min="0"
          :max-fraction-digits="2"
          adornment="€"
        />
      </div>
    </div>
    <Divider />
    <div class="space-y-4">
      <h2>{{ t('stationTransfers') }}</h2>
      <div class="grid grid-cols-3 gap-4">
        <InputField
          name="stationTransferDiscountModifier"
          :label="t('stationTransferDiscountModifier')"
          :tooltip="t('stationTransferDiscountModifierTooltip')"
          type="number"
          :min="0"
          :max-fraction-digits="2"
          :use-grouping="false"
        />
        <InputField
          name="stationTransferPriceLimitModifier"
          :label="t('stationTransferPriceLimitModifier')"
          :tooltip="t('stationTransferPriceLimitModifierTooltip')"
          type="number"
          :min="0"
          :max-fraction-digits="2"
          :use-grouping="false"
        />
      </div>
    </div>
    <Divider />
    <MinAgesForm />
    <Divider />
    <DepositsForm />
    <Divider />
    <VatRatesForm />
    <Divider />
    <DebitBookingCategoriesForm :settings-dto="settingsDto" />
    <Divider />
    <PercentageOptionsForm />
    <Divider />
    <CleaningFeesForm />
    <Divider />
    <InfringementFeesForm />
    <Divider />
    <CrossborderCountriesForm />
    <Divider />
    <InspectionShotsForm />
    <Divider />
    <div class="flex justify-end">
      <CVButton variant="default" @click="handleSave">{{ t('save') }}</CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { UpdateYieldManagementSettingsDto } from '@carvia/ros-client-types';
import { useI18n } from 'vue-i18n';
import Divider from '@/components/Divider.vue';
import { useForm } from 'vee-validate';
import DepositsForm from '@/views/yield-management/yield-management-settings/DepositsForm.vue';
import VatRatesForm from '@/views/yield-management/yield-management-settings/VatRatesForm.vue';
import DebitBookingCategoriesForm from '@/views/yield-management/yield-management-settings/DebitBookingCategoriesForm.vue';
import PercentageOptionsForm from '@/views/yield-management/yield-management-settings/PercentageOptionsForm.vue';
import CleaningFeesForm from '@/views/yield-management/yield-management-settings/CleaningFeesForm.vue';
import InfringementFeesForm from '@/views/yield-management/yield-management-settings/InfringementFeesForm.vue';
import { cloneDeep } from 'lodash';
import { usePreventLeavingUnsavedForm } from '@/hooks/use-prevent-leaving-unsaved-form.ts';
import { formHasChanged } from '@/hooks/use-form-has-changed.ts';
import InputField from '@/components/InputField.vue';
import { yieldManagementOtherSchema } from '@/validation/yield-management-other.schema.ts';
import MinAgesForm from '@/views/yield-management/yield-management-settings/MinAgesForm.vue';
import CrossborderCountriesForm from '@/views/yield-management/yield-management-settings/CrossborderCountriesForm.vue';
import InspectionShotsForm from '@/views/yield-management/yield-management-settings/InspectionShotsForm.vue';
import {
  mergeCrossborderCountriesForApi,
  splitCrossborderCountriesForForm,
} from '@/views/yield-management/yield-management-settings/crossborder-countries-form.utils';

const props = defineProps<{
  settingsDto: UpdateYieldManagementSettingsDto;
}>();

const emit = defineEmits<{
  (e: 'save', value: UpdateYieldManagementSettingsDto): void;
}>();

const { t } = useI18n();

const { crossborderCountries: _crossborderCountries, ...settingsWithoutCrossborder } =
  props.settingsDto;

let initialValues = cloneDeep({
  ...settingsWithoutCrossborder,
  ...splitCrossborderCountriesForForm(props.settingsDto),
});

const { values: formValues, handleSubmit } = useForm({
  initialValues,
  validationSchema: yieldManagementOtherSchema,
});

const handleSave = handleSubmit((values) => {
  const {
    crossborderCountryCodes,
    crossborderCountryCustomLabels,
    crossborderCountries: _crossborderCountries,
    ...rest
  } = values as UpdateYieldManagementSettingsDto & {
    crossborderCountryCodes: string[];
    crossborderCountryCustomLabels: ReturnType<
      typeof splitCrossborderCountriesForForm
    >['crossborderCountryCustomLabels'];
  };

  emit('save', {
    ...rest,
    crossborderCountries: mergeCrossborderCountriesForApi(
      crossborderCountryCodes ?? [],
      crossborderCountryCustomLabels ?? [],
    ),
  });
  initialValues = cloneDeep(formValues);
});

usePreventLeavingUnsavedForm(() => formHasChanged(initialValues, formValues));
</script>

<i18n lang="json">
{
  "en": {
    "deposits": "Deposits",
    "youngDrivers": "Young drivers",
    "youngDriverUntilAge": "Young driver until age",
    "fueling": "Fueling/Charging",
    "pricePerLiter": "Price per Liter",
    "pricePerKwh": "Price per KWh",
    "stationTransfers": "Station transfers",
    "stationTransferDiscountModifier": "Station transfer discount factor",
    "stationTransferDiscountModifierTooltip": "Multiplier on the transfer price when pickup and return are at the same station. 0.7 means 30% off.",
    "stationTransferPriceLimitModifier": "Station transfer price limit factor",
    "stationTransferPriceLimitModifierTooltip": "When pickup and return are at the same station, a transfer offer is bookable for the selected dates only if: rental price + (transfer price × discount factor) is less than rental price × this limit. Otherwise the offer stays visible with a later return date that meets the limit. 1.5 means the discounted transfer may add at most 50% on top of the rental price."
  },
  "de": {
    "deposits": "Kautionen",
    "youngDrivers": "Jungfahrer",
    "youngDriverUntilAge": "Jungfahrer bis Alter",
    "fueling": "Tanken/Laden",
    "pricePerLiter": "Preis pro Liter",
    "pricePerKwh": "Preis pro kWh",
    "stationTransfers": "Stations-Transfers",
    "stationTransferDiscountModifier": "Rabattfaktor für Stations-Transfers",
    "stationTransferDiscountModifierTooltip": "Faktor auf den Transferpreis, wenn Abholung und Rückgabe an derselben Station liegen. 0,7 entspricht 30 % Rabatt.",
    "stationTransferPriceLimitModifier": "Preisgrenze für Stations-Transfer-Angebote",
    "stationTransferPriceLimitModifierTooltip": "Wenn Abholung und Rückgabe an derselben Station sind, ist ein Transfer-Angebot für die gewählten Daten nur buchbar, wenn: Mietpreis + (Transferpreis × Rabattfaktor) kleiner als Mietpreis × dieser Grenze ist. Sonst bleibt das Angebot sichtbar mit einem späteren Rückgabedatum, das die Grenze erfüllt. 1,5 bedeutet, dass der rabattierte Transfer höchstens 50 % auf den Mietpreis aufschlagen darf."
  },
  "es": {
    "deposits": "Depósitos",
    "youngDrivers": "Conductores jóvenes",
    "youngDriverUntilAge": "Conductor joven hasta la edad",
    "fueling": "Repostaje/carga",
    "pricePerLiter": "Precio por litro",
    "pricePerKwh": "Precio por kWh",
    "stationTransfers": "Traslados entre estaciones",
    "stationTransferDiscountModifier": "Factor de descuento para traslados entre estaciones",
    "stationTransferDiscountModifierTooltip": "Multiplicador del precio de traslado cuando la recogida y la devolución son en la misma estación. 0,7 equivale a un 30 % de descuento.",
    "stationTransferPriceLimitModifier": "Límite de precio para ofertas con traslado entre estaciones",
    "stationTransferPriceLimitModifierTooltip": "Si la recogida y la devolución son en la misma estación, una oferta con traslado solo se puede reservar para las fechas elegidas si: precio de alquiler + (precio de traslado × factor de descuento) es menor que el precio de alquiler × este límite. Si no, la oferta sigue visible con una fecha de devolución posterior que cumple el límite. 1,5 significa que el traslado con descuento puede añadir como máximo un 50 % al precio de alquiler."
  }
}
</i18n>
